import { useCallback, useEffect, useState } from 'react';
import { create } from 'zustand';
import { isStaleWrite, type TriageItem } from '@signalvote/shared';

/**
 * The stale-row prompt for the inline body EDITORS (name/designation/payee/…),
 * which submit with a manual Save and have no version-reconciling retry — so a
 * refresh really is the recovery, and the copy says so.
 *
 * The reversible quick-actions use {@link SAVE_FAILED_MESSAGE} instead: they pair
 * with a Retry that re-reads the reconciled version, so "refresh" would be wrong
 * advice (Retry does it for you) — and treasurers don't think in "updated
 * elsewhere" jargon. There the copy is a plain, blameless "that didn't take".
 */
export const REFRESH_PROMPT =
  'This item was updated elsewhere — refresh to see the latest.';

/**
 * What a rejected reversible quick-action shows next to its Retry. Deliberately
 * plain and cause-agnostic: the treasurer just needs to know THIS click didn't
 * land (so they don't move on believing it did) — Retry is the recovery.
 */
export const SAVE_FAILED_MESSAGE = "That didn't save.";

/**
 * Row-scoped inline-error state for the reversible ledger quick-actions, keyed by
 * the row's error id. It lives ABOVE the control in an external store — not in the
 * control's own `useState` — for one specific reason: an optimistic status flip
 * (#266) swaps the control that initiated the action (e.g. ConfirmCheckbox →
 * DoneActionCell), unmounting the initiator before the server's rejection lands.
 * Control-local error state is lost on that unmount, so the row snapped back
 * SILENTLY. Keeping the error here lets the re-mounted control (same id) read it.
 */
interface RowErrorState {
  errors: Record<string, string | null>;
  setError: (id: string, message: string | null) => void;
  clearError: (id: string) => void;
}

export const useRowErrorStore = create<RowErrorState>()((set) => ({
  errors: {},
  setError: (id, message) =>
    set((state) => ({ errors: { ...state.errors, [id]: message } })),
  clearError: (id) =>
    set((state) => ({ errors: { ...state.errors, [id]: null } })),
}));

/**
 * Whether a reversible quick-action is currently in flight for a row, keyed the
 * same as the error store (per action-slot id). It lives in an external store for
 * the SAME reason the error does: the optimistic status flip swaps the initiating
 * control (ConfirmCheckbox → DoneActionCell) mid-flight, so control-local `isPending`
 * dies on that unmount. Keeping the flag here lets the re-mounted twin stay disabled
 * until the action settles — which is what stops a rapid select/deselect from firing
 * a SECOND overlapping mutation carrying a now-stale concurrencyVersion (the 409 that
 * produced the un-retryable "That didn't save"). Cleared on settle, when the row has
 * re-rendered with the reconciled version.
 */
interface RowActionPendingState {
  pending: Record<string, boolean>;
  setPending: (id: string, value: boolean) => void;
}

export const useRowActionPendingStore = create<RowActionPendingState>()((set) => ({
  pending: {},
  setPending: (id, value) =>
    set((state) => ({ pending: { ...state.pending, [id]: value } })),
}));

/**
 * Rows the treasurer just marked "reported" this session, which show the inline Undo.
 *
 * Why a store rather than the action component's own state: marking is optimistic
 * (#266) — the flip stamps `ledgerStatus: 'Reported'` immediately, which makes the
 * row's `canOfferMarkAsReported` gate go false and would unmount the action (and
 * its Undo) the instant it succeeds. Holding the mark here keeps the acknowledgment
 * + Undo alive for its window, independent of that flip; the entry is cleared on Undo.
 */
interface JustMarkedReportedState {
  marked: Record<string, true>;
  setJustMarked: (id: string) => void;
  clearJustMarked: (id: string) => void;
}

export const useJustMarkedReportedStore = create<JustMarkedReportedState>()(
  (set) => ({
    marked: {},
    setJustMarked: (id) =>
      set((state) => ({ marked: { ...state.marked, [id]: true } })),
    clearJustMarked: (id) =>
      set((state) => {
        const next = { ...state.marked };
        delete next[id];
        return { marked: next };
      }),
  }),
);

/**
 * The Expenditure created by the most recent one-click Accept of a classification hint this
 * session — the only row that offers Undo. A store because Accept lives on the bank line's row,
 * which unmounts as the line refetches into the Expenditure row that shows the Undo.
 */
interface LastAcceptedHintState {
  expenditureId: string | null;
  setLastAccepted: (expenditureId: string) => void;
  clearLastAccepted: () => void;
}

export const useLastAcceptedHintStore = create<LastAcceptedHintState>()((set) => ({
  expenditureId: null,
  setLastAccepted: (expenditureId) => set({ expenditureId }),
  clearLastAccepted: () => set({ expenditureId: null }),
}));

/** Whether this row is the Expenditure the most recent classification-hint Accept created. */
export function useIsLastAcceptedHint(item: TriageItem): boolean {
  return useLastAcceptedHintStore(
    (state) => item.kind === 'Expenditure' && state.expenditureId === item.expenditure.id,
  );
}

/** Whether the row was just marked reported and still offers its Undo. */
export function useIsJustMarkedReported(id: string): boolean {
  return useJustMarkedReportedStore((state) => state.marked[id] === true);
}

/**
 * Shared runner for a reversible ledger quick-action: clears the row's inline
 * error, runs the mutation, and on rejection maps the failure to treasurer copy
 * (a concurrency conflict becomes the stale-row refresh prompt; everything else
 * uses the thrown message or the caller's fallback).
 *
 * The returned `errorMessage` is read from the row-scoped store, so it survives
 * the optimistic control swap — the fix for the silent rollback.
 */
export function useQuickAction(errorId: string) {
  const errorMessage = useRowErrorStore((state) => state.errors[errorId] ?? null);
  const setError = useRowErrorStore((state) => state.setError);
  const isRunning = useRowActionPendingStore(
    (state) => state.pending[errorId] ?? false,
  );
  const setPending = useRowActionPendingStore((state) => state.setPending);

  const run = useCallback(
    async (mutate: () => Promise<unknown>, fallback: string) => {
      setError(errorId, null);
      // Mark the row in flight BEFORE awaiting, so the twin that swaps in on the
      // optimistic flip mounts already-disabled — the rapid-double-click guard.
      // Cleared in `finally` even though the initiating control has unmounted by
      // then (the async runner outlives it), once the settle has reconciled.
      setPending(errorId, true);
      try {
        await mutate();
      } catch (err) {
        if (isStaleWrite(err)) {
          setError(errorId, SAVE_FAILED_MESSAGE);
          return;
        }
        setError(errorId, err instanceof Error ? err.message : fallback);
      } finally {
        setPending(errorId, false);
      }
    },
    [errorId, setError, setPending],
  );

  // `errorId` is returned so the row's <RowError> testid matches the store slot —
  // a row can host several independent action controls (leading status control,
  // trailing exclude/restore, expanded mark-reported), each with its own slot.
  // `isRunning` is the row-scoped in-flight flag (survives the control swap) that
  // controls disable on to prevent overlapping mutations from a rapid re-click.
  return { errorMessage, run, errorId, isRunning };
}

/**
 * The inline, row-level failure surface for a reversible ledger action. When the
 * action can be retried (see {@link useQuickAction}), `onRetry` re-issues it —
 * one click on the row instead of re-hunting for the control. Because the retry
 * re-invokes the control's live action, it carries the row's reconciled
 * concurrency version (the invalidate-on-settle refetch already pulled it), so a
 * stale-version conflict does not just re-conflict.
 */
export function RowError({
  id,
  message,
  onRetry,
}: {
  id: string;
  message: string | null;
  onRetry?: () => void;
}) {
  if (!message) return null;
  return (
    <p
      role="alert"
      data-testid={`triage-ledger-error-${id}`}
      className="mt-1 text-xs text-rose-700"
    >
      {message}
      {onRetry && (
        <button
          type="button"
          data-testid={`triage-ledger-retry-${id}`}
          onClick={onRetry}
          className="ml-2 font-medium text-rose-800 underline hover:text-rose-900"
        >
          Retry
        </button>
      )}
    </p>
  );
}
